import type { BanDich } from '../../types';
import type { viNktvSuDungAi } from './su-dung-ai.vi';

export const enNktvSuDungAi: Pick<BanDich, keyof typeof viNktvSuDungAi> = {
  nktvSuDungAi: {
    tieuDe: 'AI usage log',
    cotTenant: 'Tenant',
    cotGiaVon: 'Cost price',
    tatCaTenant: 'All tenants',
    tab: {
      tongQuan: 'Overview',
      trace: 'By trace',
      luotGoi: 'Model calls',
      luong: 'By business object',
    },
    loc: {
      moHinh: 'Model',
      nhaCungCap: 'Provider',
      service: 'Service',
      tinhNang: 'Feature',
      nguoiDung: 'User',
      donVi: 'Unit',
      trangThai: 'Cost price status',
      trangThaiDaTinh: 'Cost calculated',
      trangThaiChuaCoGia: 'No cost price',
      coCanhBao: 'Token warning',
      coCanhBaoCo: 'With warning',
      coCanhBaoKhong: 'Without warning',
      loiGoiY: 'Could not load filter suggestions — some values may be missing from the lists.',
    },
    danhSach: {
      sapXep: 'Sort by',
      moiNhat: 'Newest',
      tonNhat: 'Most tokens',
      xepHangMotTrang:
        '"Most tokens" is a single-page ranking — it cannot load more. To browse all data, switch back to "Newest".',
      het: 'Showing all {{so}} rows that match this filter.',
      rongLoc: 'No rows match the filter',
    },
    trace: {
      bang: 'Processing traces',
      goiY: 'Each row is one processing chain: a question, a document scan. Click a row to see its steps.',
      cotBatDau: 'Started',
      cotChuoi: 'Trace',
      cotNguoi: 'Performed by',
      cotService: 'Services',
      cotBuoc: 'Steps',
      cotMoHinh: 'Models',
      cotTietKiem: 'Cache savings',
      cotKeoDai: 'Duration',
      cotMo: 'Open',
      soLoi: '{{so}} errors',
      hoiChatbot: 'Asked chatbot {{ten}}',
      tieuDeCoAi: 'Trace with {{so}} AI calls',
      maTraCuuNgan: 'Trace ID {{ma}}…',
      moChuoi: 'Open the trace to see each step',
      rongTieuDe: 'No processing traces',
      rongMoTa: 'There are no traces in this time range. Try widening the time range above.',
      cotLuotAi: 'AI calls',
      cotLoi: 'Errors',
      cotTinCay: 'Reliability',
    },
    luotGoi: {
      bang: 'Model calls',
      cotThoiGian: 'Time',
      cotMoHinh: 'Model',
      cotTinhNang: 'Feature',
      cotNguoi: 'User / object',
      cotTietKiem: 'Cache savings',
      cotDoTre: 'Latency',
      cotTinCay: 'Reliability',
      cotChuoi: 'Trace',
      xemChuoi: 'View trace',
      rongTieuDe: 'No model calls',
      rongMoTa: 'There are no calls in this time range. Try widening the time range above.',
    },
    luong: {
      bang: 'Business objects by tokens',
      cotDoiTuong: 'Object',
      cotKhoang: 'From → to',
      cotNguoi: 'Users',
      cotService: 'Services',
      loai: 'Object type',
      loaiTatCa: 'All',
      cacTrace: 'Processing traces of this object — click an ID to see each step:',
      khongCoTrace: 'The aggregated record does not include the list of trace IDs.',
      motTrang: 'Showing {{so}} objects, sorted by total tokens descending. This is a single-page ranking; it cannot load more.',
      rongTieuDe: 'No business objects',
      rongMoTa: 'No business object was processed in this range. Try widening the time range above.',
      cotSoChuoi: 'Chains',
    },
    chuoi: {
      tieuDe: 'Timeline of a processing trace',
      goiMoHinh: 'Model call',
      luotGoiAi: 'AI calls',
      batDauSau: 'starts after {{lech}}',
      moChiTiet: 'Click to see this step’s details',
      goiYMo: 'Click a step to see its details.',
      cotBuoc: 'Step',
      luc: 'Time',
      nguoi: 'Performed by',
      dichVu: 'Service',
      doiTuong: 'Object',
      moHinh: 'Model',
      tietKiem: 'Cache savings',
      bocTach: 'Seven-type token breakdown',
      maTraCuu: 'Trace ID (trace_id)',
      keoDai: 'Duration',
      soBuoc: 'Steps',
      lech: '{{lech}} after start',
      doTre: 'latency {{doTre}}',
      khongKhaiDoTre: 'this step did not record a latency',
      thanhToken: 'Step tokens: {{so}}',
      biCat:
        'The trace is longer than the 200-step limit, so the list below is truncated — the last steps are not shown, and the total tokens only count the loaded part.',
      chuGiaiThuong: 'Business step',
      chuGiaiLlm: 'Model call',
      chuGiaiLoi: 'Failed step',
      chuGiaiToken: 'Tokens of the step (relative to the largest step)',
      chuGiaiVach: 'Thin mark = the step did not record a latency, only its time.',
      rongTieuDe: 'The trace has no steps',
      rongMoTa: 'No record with this trace ID is within the scope you can view.',
      foxVao: 'Fox tokens in',
      foxRa: 'Fox tokens out',
      foxPhu: 'In {{vao}} · out {{ra}}',
    },
    token: {
      thanhNhan: 'Share of the seven token types in this call',
      dau_vao: 'Input',
      dau_vao_anh: 'Image input',
      dau_vao_tool: 'Tool input',
      dau_vao_dem: 'Cache read',
      ghi_dem: 'Cache write',
      dau_ra: 'Output',
      suy_luan: 'Reasoning',
      congBay: 'Sum of seven types:',
      tongBao: 'Total reported by the system:',
      khongBao: 'The provider did not report this type — not the same as zero.',
      soKhongBao: '{{so}} types not reported',
      lechPhanHoach:
        'The sum of the seven types differs from the recorded total by {{lech}} tokens. They must add up exactly to the total, so this is a token normalizer bug — do not use this row for final figures.',
    },
    co: {
      chuaCoGia: 'No cost price',
      chuaCoGiaMoTa: 'This model has no cost price yet, so the cost total is missing that part. Token counts are still correct.',
      khaiGia: 'Open model cost prices',
      khaiGiaMoTa: 'Open model cost prices to enter a price for {{moHinh}}',
      thieuTyGia: 'Missing exchange rate',
      thieuTyGiaMoTa: 'The model has a cost price but no exchange rate for the date of this call, so the cost is blank. Token counts are still correct.',
      xemTyGia: 'View exchange rates',
      xemTyGiaMoTa: 'Open model cost prices, where rates are entered',
      canhBao: 'Conversion warnings: {{so}}',
      canhBaoMoTa:
        'The token normalizer raised a warning about this call’s own token conversion, so the figures are not reliable enough to finalize. To fix: ask the engineering team to correct the token normalizer.',
      uocTinh: 'estimated price',
      uocTinhMoTa: 'Cost uses an estimated price, not a declared one.',
      khongDangTin: 'Not reliable yet',
      khongDangTinMoTa:
        'At least one call in the trace carries a token conversion warning, so the trace total is not used for final figures.',
      tietKiemAm:
        'Negative value: reading back from the cache cost more than a fresh call. This is the real result of the calculation, not a display error.',
    },
    tongQuan: {
      khongLuotTieuDe: 'No model calls',
      khongLuotMoTa:
        'No model call was traced in the selected period. Try widening the time range, or check whether the services in use send AI call traces.',
      soLuot: 'Model calls',
      soLuotPhu: 'In {{trace}} traces.',
      soMoHinh: 'Models',
      soMoHinhPhu: 'With calls in this period.',
      tokenFox: 'Fox tokens',
      tokenFoxPhu: 'Input + output',
      foxVao: 'Fox tokens in',
      foxRa: 'Fox tokens out',
      tyLeTrenTong: '{{tyLe}} of total Fox tokens',
      tokenThatPhu: 'All seven token types.',
      giaVonPhu: 'Fixed when recorded.',
      giaVonChuaDu: '{{so}} unpriced calls.',
    },
    bieuDo: {
      tokenTieuDe: 'Provider tokens over time, by seven types',
      tokenMoTa:
        'The seven token types do not overlap: every token belongs to exactly one type, so the seven parts add up exactly to the total.',
      tokenNhan: 'Stacked columns: tokens over time, split into seven types',
      tokenKhop: 'The seven parts add up to {{bay}} tokens and audit-service recorded a total of {{tong}} — they match.',
      tokenLech:
        'The seven parts add up to {{bay}} tokens but audit-service recorded a total of {{tong}}, and {{moc}} time buckets are off. The seven token types no longer add up to the total: the fault is in the token normalizer, not in the chart. See each call’s warnings in the Model calls tab.',
      tongToken: 'Total provider tokens',
      soTokenThe: '{{so}} tokens',
      soLuot: 'Calls',
      tietKiemThe: 'Savings',
      tietKiemTieuDe: 'Cache savings',
      tietKiemMoTa: 'Amount saved by reading back from the cache, minus the amount paid to write to the cache.',
      tietKiemNhan: 'Columns: cache savings over time, may be negative',
      tietKiemAm:
        'A negative number is not an error: cache writes currently cost more than the savings, i.e. writes were paid for but not reused enough to break even. Negative values are shown with their sign, not clamped to 0.',
      tietKiemPhiLuu: 'Hourly cache storage fees are not included — they are outside the formula.',
      foxTieuDe: 'Fox tokens over time',
      foxNhan: 'Stacked bars: Fox tokens over time, split into input and output',
      foxTong: 'Total Fox tokens',
      giaVonTieuDe: 'Cost price over time',
      giaVonMoTa: 'At the cost price in effect when recorded.',
      giaVonNhan: 'Bars: cost price over time',
      giaVonChuaDu: '{{so}} calls in this period have no cost price and are not counted in these bars.',
    },
    dichVu: {
      audit: 'Logs & Traces',
      baoCao: 'Business reports',
      ekyc: 'eKYC',
      index: 'Knowledge index',
      ocr: 'Document processing',
      rpa: 'RPA management',
      soTayViec: 'Task handbook',
      thamDinh: 'AI appraisal',
      triThuc: 'AI knowledge',
      troLyAi: 'AI assistant',
      vanBan: 'Document handling',
    },
    tinhNang: {
      ocrQuet: 'Document scan (OCR)',
      ocrDocTrang: 'Read a document page (OCR)',
      ocrDocTruoc: 'Pre-read document (OCR)',
      ocrDeXuatSchema: 'Suggest document setup (OCR)',
      troLySinhSql: 'Data assistant · SQL generation',
      troLySoanLoi: 'Data assistant · answer drafting',
      troLyDocThanhTieng: 'Data assistant · read aloud',
      troLyTraWeb: 'Data assistant · web search',
      indexCauTruc: 'Build document structure',
      indexDocAnh: 'Read scanned document',
      ekycBocTach: 'eKYC document extraction',
      thamDinhHoiDap: 'Appraisal Q&A',
      thamDinhDanhGia: 'Write appraisal report',
      thamDinhBanSua: 'Suggest file revisions',
      thamDinhGoiY: 'Suggest appraisal criteria',
      thamDinhCham: 'Score appraisal file',
      thamDinhCanTrongSo: 'Balance criteria weights',
      vanBanTomTat: 'Summarize document',
      vanBanSoanThao: 'Draft document',
      vanBanDienTruong: 'Fill document fields',
      vanBanPhanCong: 'Suggest assignment',
      vanBanPhanRa: 'Break down task',
      vanBanDocPdfQuet: 'Read scanned PDF',
      vanBanXemTruocMau: 'Preview document template',
    },
    phanRa: {
      tieuDe: 'Breakdown by group',
      moTa: 'Each grouping shows the 50 rows with the most tokens.',
      nhom: 'Group by',
      bang: 'Token breakdown by group',
      theo_tenant: 'Tenant',
      theo_mo_hinh: 'Model',
      theo_service: 'Service',
      theo_tinh_nang: 'Feature',
      theo_don_vi: 'Unit',
      theo_nguoi_dung: 'User',
      rongTieuDe: 'No data in this period',
      rongMoTa: 'No record falls in the selected range. Try widening the time range.',
      cotKhoa: 'Group',
      cotSoLuot: 'Calls',
      cotDoTre: 'Avg. latency',
      doTreTrong: 'No call recorded a latency — not the same as 0 ms',
      cotTyLeLoi: 'Error rate',
      tyLeLoiMoTa: '{{loi}} failed out of {{tong}} calls',
      chuaCoGia: '{{so}} without cost price',
      chuaCoGiaMoTa: 'Some calls have no cost yet because a price line or exchange rate is missing.',
      coCanhBao: 'Warnings: {{so}}',
      coCanhBaoMoTa: 'Some calls carry a token conversion warning — this row’s token count is not reliable yet',
    },
    chuThichTenant: 'The official figure is the "AI tokens this month" card. This log may differ by a few tokens due to rounding.',
    cotFox: 'Fox tokens',
    foxVao: 'Input',
    foxRa: 'Output',
    foxTong: 'Total',
    cotTokenThat: 'Provider tokens',
  },
};
